import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Feather, Calendar, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import SharePoemButton from './SharePoemButton';
import { getPoemOfTheWeek } from '../data/poems';

const PoemOfTheWeekHome = () => {
  const poem = useMemo(() => getPoemOfTheWeek(), []);

  return (
    <section id="poem-of-the-week" className="relative py-24 bg-gradient-to-b from-[#0a0a0a] via-[#0f0f0f] to-[#0a0a0a] overflow-hidden">
      {/* Decorative background flourish */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80vw] h-[80vw] max-w-[900px] max-h-[900px] rounded-full bg-[#D4AF37]/5 blur-3xl" />
      </div>

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1 mb-4">
            <Calendar className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-xs tracking-wider">POEM OF THE WEEK</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            A verse to sit with{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              this week
            </span>
          </h2>
        </div>

        <article
          className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-2 border-[#D4AF37]/30 rounded-3xl p-8 md:p-14 shadow-2xl shadow-[#D4AF37]/10"
          data-testid="home-poem-of-the-week"
        >
          <Link to={`/poetry/${poem.id}`} className="block group">
            <h3
              className="text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700] mb-2 leading-tight group-hover:opacity-90 transition-opacity"
              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
              data-testid="home-potw-title"
            >
              {poem.title}
            </h3>
          </Link>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-gray-500 text-sm mb-8">
            <span>by {poem.author}</span>
            <span className="flex flex-wrap gap-1">
              {poem.emotions.map((e) => (
                <span
                  key={e}
                  className="inline-block text-[10px] uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/30 rounded-full px-2 py-0.5"
                >
                  {e.replace('-', ' ')}
                </span>
              ))}
            </span>
          </div>

          <div className="text-xl md:text-2xl text-gray-100 leading-relaxed font-serif whitespace-pre-line mb-8">
            {poem.verses.join('\n')}
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-[#D4AF37]/10">
            <SharePoemButton poem={poem} />
            <Link to={`/poetry/${poem.id}`} data-testid="home-potw-permalink">
              <Button
                variant="outline"
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a0a0a] font-bold"
              >
                READ IN FULL
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/poetry" data-testid="home-potw-room-link" className="ml-auto text-sm text-gray-400 hover:text-[#D4AF37] inline-flex items-center gap-1 font-medium">
              <Feather className="w-4 h-4" />
              Enter the Poetry Room
            </Link>
          </div>
        </article>

        <p className="text-center text-xs text-gray-600 mt-6">
          A new poem rises each week. Written by J&rsquo;Wells, founder of 40 Songs and a Mic.
        </p>
      </div>
    </section>
  );
};

export default PoemOfTheWeekHome;
